import {Configuration, RfpBidOptRequest, RfpOptimizedApi, RfpLaneCarrierOptResponse} from '@shipwell/backend-core-sdk';
import {AxiosResponse} from 'axios';
import {basePath} from 'App/api/config';
import {getAccessToken} from 'App/api/utils';

function createRfpOptimizedApi() {
  return new RfpOptimizedApi(
    new Configuration({
      basePath,
      apiKey: getAccessToken
    })
  );
}

type RfpBidParameters = Parameters<typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptGet>;
type RfpBidOptions = {
  page: RfpBidParameters[0];
  pageSize: RfpBidParameters[1];
};

export const getOptimizedRfpBids = async (opts: RfpBidOptions = {page: 1, pageSize: 1000}) => {
  const axiosResponse = await createRfpOptimizedApi().rfpOptimizedRfpBidOptGet(opts.page, opts.pageSize);
  return axiosResponse.data;
};

export const getOptimizedRfpDetails = (rfpBidId: string) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdGet(rfpBidId);
};

type RfpLaneParameters = Parameters<typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptRfpBidOptIdRfpLaneOptsGet>;
type RfpLaneOptions = {
  page?: RfpLaneParameters[1];
  pageSize?: RfpLaneParameters[2];
  ordering?: RfpLaneParameters[3];
  laneId?: RfpLaneParameters[4];
  laneIdContains?: RfpLaneParameters[5];
  originCity?: RfpLaneParameters[6];
  originCityContains?: RfpLaneParameters[7];
  originState?: RfpLaneParameters[8];
  originStateContains?: RfpLaneParameters[9];
  originPostCode?: RfpLaneParameters[10];
  originPostCodeContains?: RfpLaneParameters[11];
  destinationCity?: RfpLaneParameters[12];
  destinationCityContains?: RfpLaneParameters[13];
  destinationState?: RfpLaneParameters[14];
  destinationStateContains?: RfpLaneParameters[15];
  destinationPostCode?: RfpLaneParameters[16];
  destinationPostCodeContains?: RfpLaneParameters[17];
  volume?: RfpLaneParameters[18];
  lanePolicyName?: RfpLaneParameters[19];
  lanePolicyNameContains?: RfpLaneParameters[20];
  priceVolatilityText?: RfpLaneParameters[21];
  marketGraph?: RfpLaneParameters[22];
  q?: RfpLaneParameters[23];
};

export const getOptimizedRfpLanes = (rfpBidOptId: string, opts: RfpLaneOptions = {page: 1, pageSize: 20}) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdRfpLaneOptsGet(
    rfpBidOptId,
    opts?.page,
    opts?.pageSize,
    opts?.ordering,
    opts?.laneId,
    opts?.laneIdContains,
    opts?.originCity,
    opts?.originCityContains,
    opts?.originState,
    opts?.originStateContains,
    opts?.originPostCode,
    opts?.originPostCodeContains,
    opts?.destinationCity,
    opts?.destinationCityContains,
    opts?.destinationState,
    opts?.destinationStateContains,
    opts?.destinationPostCode,
    opts?.destinationPostCodeContains,
    opts?.volume,
    opts?.lanePolicyName,
    opts?.lanePolicyNameContains,
    opts?.priceVolatilityText,
    opts?.marketGraph,
    opts?.q
  );
};

export const createOptimizedRfpBid = (bidRequest: RfpBidOptRequest) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptPost(bidRequest);
};

type UpdateRfpBid = Parameters<typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptRfpBidOptIdPut>;
export type UpdateRfpParams = {
  rfpBidOptId: UpdateRfpBid[0];
  bidExpiryDateTime?: UpdateRfpBid[1]['bid_expiry_date_time'];
  endDate: UpdateRfpBid[1]['end_date'];
  startDate: UpdateRfpBid[1]['start_date'];
  name: UpdateRfpBid[1]['name'];
  notes?: UpdateRfpBid[1]['notes'];
  archived?: UpdateRfpBid[1]['archived'];
};

export const updateOptimizedRfpBid = (rfp: UpdateRfpParams) => {
  const {rfpBidOptId, bidExpiryDateTime, endDate, startDate, name, notes, archived} = rfp;
  const updatedBid: UpdateRfpBid[1] = {
    bid_expiry_date_time: bidExpiryDateTime,
    archived,
    end_date: endDate,
    start_date: startDate,
    name,
    notes
  };
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdPut(rfpBidOptId, updatedBid);
};

export const deleteOptimizedRfpBid = (rfpBidOptId: string) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdDelete(rfpBidOptId);
};

type RfpCarrierBidParameters = Parameters<typeof RfpOptimizedApi.prototype.rfpOptimizedCarrierBidRequestsGet>;
type RfpCarrierBidOptions = {
  page: RfpCarrierBidParameters[0];
  pageSize: RfpCarrierBidParameters[1];
};

export const getOptimizedRfpCarrierBidRequests = async (opts: RfpCarrierBidOptions = {page: 1, pageSize: 1000}) => {
  const axiosResopnse = await createRfpOptimizedApi().rfpOptimizedCarrierBidRequestsGet(opts.page, opts.pageSize);
  return axiosResopnse.data;
};

export const getOptimizedRfpCarrierBidDetails = (rfpCarrierBidRequestOptId: string) => {
  return createRfpOptimizedApi().rfpOptimizedCarrierBidRequestsRfpCarrierBidRequestOptIdGet(rfpCarrierBidRequestOptId);
};

type BidRequestParameters = Parameters<
  typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptRfpBidOptIdCarrierBidRequestPost
>;
type BidRequest = {
  rfpBidOptId: BidRequestParameters[0];
  bidRequestState: BidRequestParameters[1]['bid_request_state'];
  carrierCompany: BidRequestParameters[1]['carrier_company'];
  pocEmail: BidRequestParameters[1]['poc_email'];
};

export const createOptimizedBidRequest = (bid: BidRequest) => {
  const {rfpBidOptId, bidRequestState, carrierCompany, pocEmail} = bid;
  const bidRequestObj: BidRequestParameters[1] = {
    bid_request_state: bidRequestState,
    carrier_company: carrierCompany,
    rfp_bid_opt: rfpBidOptId,
    poc_email: pocEmail
  };
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdCarrierBidRequestPost(rfpBidOptId, bidRequestObj);
};

export const getRfpDocuments = (rfpBidOptId: string, opts = {page: 1, pageSize: 20}) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdRfpBidOptDocumentsGet(
    rfpBidOptId,
    opts.page,
    opts.pageSize
  );
};

export const getRfpCarrierDocuments = (rfpCarrierBidRequestOptId: string, opts = {page: 1, pageSize: 20}) => {
  return createRfpOptimizedApi().rfpOptimizedCarrierBidRequestsRfpCarrierBidRequestOptIdCarrierBidReqDocumentsGet(
    rfpCarrierBidRequestOptId,
    opts.page,
    opts.pageSize
  );
};

type CreateRfpDocumentParameters = Parameters<
  typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptRfpBidOptIdRfpBidOptDocumentsPost
>;
type CreateRfpDocument = {
  rfpBidOptId: CreateRfpDocumentParameters[0];
  description: CreateRfpDocumentParameters[1];
  file: CreateRfpDocumentParameters[2];
};

export const createRfpDocument = (document: CreateRfpDocument) => {
  // SDK returns file as any
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {rfpBidOptId, description, file} = document;
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdRfpBidOptDocumentsPost(rfpBidOptId, description, file);
};

export const getRfpCarrierCsv = (rfpCarrierBidRequestOptId: string) => {
  return createRfpOptimizedApi().rfpOptimizedCarrierBidRequestsRfpCarrierBidRequestOptIdDownloadCsvGet(
    rfpCarrierBidRequestOptId
  );
};

export const getRfpCsv = (rfpBidOptId: string) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdExportCsvGet(rfpBidOptId);
};

type GetCarrierWithDataMetricsParameters = Parameters<
  typeof RfpOptimizedApi.prototype.rfpOptimizedDataMetricsCarrierSelectionGet
>;
type GetCarrierWithDataMetrics = {
  page?: GetCarrierWithDataMetricsParameters[0];
  pageSize?: GetCarrierWithDataMetricsParameters[1];
  q?: GetCarrierWithDataMetricsParameters[2];
  tags?: GetCarrierWithDataMetricsParameters[3];
};

export const getCarrierWithDataMetrics = (params: GetCarrierWithDataMetrics) => {
  const {page, pageSize, q, tags} = params;
  return createRfpOptimizedApi().rfpOptimizedDataMetricsCarrierSelectionGet(page, pageSize, q, tags);
};

// backend missing this, remove once they update type
export interface RfpLaneCarrierOptResponseExtended extends RfpLaneCarrierOptResponse {
  lane_carriers_without_bids?: string[];
}

export const getRfpLaneBids = (rfpLaneOptId: string, rfpBidOptId: string) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdRfpLaneOptsRfpLaneOptIdCarrierLanesGet(
    rfpLaneOptId,
    rfpBidOptId
  ) as Promise<AxiosResponse<RfpLaneCarrierOptResponseExtended[]>>;
};

type CreateRoutingGuidesParameters = Parameters<
  typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptRfpBidOptIdCreateContractsRgPost
>;

type CreateRoutingGuidesParams = {
  rfpBidOptId: CreateRoutingGuidesParameters[0];
  workflowTenderExpirySeconds: CreateRoutingGuidesParameters[1]['workflow_tender_expiry_seconds'];
  fuelSurchargeId?: CreateRoutingGuidesParameters[1]['fuel_surcharge_id'];
};
export const createRoutingGuides = ({
  rfpBidOptId,
  workflowTenderExpirySeconds,
  fuelSurchargeId
}: CreateRoutingGuidesParams) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdCreateContractsRgPost(rfpBidOptId, {
    workflow_tender_expiry_seconds: workflowTenderExpirySeconds,
    fuel_surcharge_id: fuelSurchargeId
  });
};

type CreateContractsParameters = Parameters<
  typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptRfpBidOptIdCreateContractsRgPost
>;
type CreateContractsParams = {
  rfpBidOptId: CreateContractsParameters[0];
  fuelSurchargeId?: CreateContractsParameters[1]['fuel_surcharge_id'];
};
export const createContracts = ({rfpBidOptId, fuelSurchargeId}: CreateContractsParams) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdCreateOnlyContractsPost(rfpBidOptId, {
    fuel_surcharge_id: fuelSurchargeId
  });
};

type UpdateRfpLaneBid = Parameters<
  typeof RfpOptimizedApi.prototype.rfpOptimizedRfpBidOptRfpBidOptIdRfpLaneOptsRfpLaneOptIdCarrierLanesPut
>;

export type UpdateRfpLaneBidParams = {
  rfpLaneOptId: UpdateRfpLaneBid[0];
  rfpBidOptId: UpdateRfpLaneBid[1];
  orderedBids: RfpLaneCarrierOptResponse[];
};

export const updateRfpLaneBid = (params: UpdateRfpLaneBidParams) => {
  const {rfpLaneOptId, rfpBidOptId, orderedBids} = params;
  const requestBody = orderedBids.map((bid) => ({lane_carrier_id: bid.id, order_index: bid.order_index}));
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdRfpLaneOptsRfpLaneOptIdCarrierLanesPut(
    rfpLaneOptId,
    rfpBidOptId,
    requestBody
  );
};

export const getContractsWorkflowCreationProgress = (rfpBidOptId: string) => {
  return createRfpOptimizedApi().rfpOptimizedRfpBidOptRfpBidOptIdRfpLaneContractsWorkflowCreationProgressGet(
    rfpBidOptId
  );
};
